var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from "axios";
import { axiosPrivate } from "../../api/axios";
/**
 * Create a new payment method via POST request.
 *
 * Endpoint: POST /CreatePaymentMethod (legacy endpoint)
 *
 * The backend determines create behavior based on PaymentProcessor:
 * - STRIPE: Uses stripePaymentMethodId
 * - AUTHORIZE: Uses token
 * - TAP: Uses tap-specific fields
 *
 * This function returns a PaymentMethodsResponse to properly update the SWR cache.
 * After the mutation, we optimistically add the new payment method to the cache.
 *
 * @param key - SWR cache key (not used in mutation, but required by useSWRMutation)
 * @param options - Contains the payment method request and current cached data
 * @returns A PaymentMethodsResponse with the updated paymentMethods array
 */
export const createPaymentMethod = (key, { arg }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    // Use legacy endpoint with POST and payload
    const response = yield axiosPrivate.post("/CreatePaymentMethod", arg.request);
    if (!response.data || !response.data.success) {
        throw new Error(((_a = response.data) === null || _a === void 0 ? void 0 : _a.message) || "Failed to create payment method");
    }
    // Build the new payment method from the response (legacy returns { success, message, card })
    const newPaymentMethod = response.data.card;
    // Update the cache optimistically by adding the new payment method to the current data
    const currentData = arg.currentData;
    if (!currentData) {
        // No current data, return a minimal response with just the new payment method
        return {
            success: true,
            paymentMethods: [newPaymentMethod],
            giftCards: [],
            enableGiftCards: false,
        };
    }
    const paymentMethods = currentData.paymentMethods || [];
    // If this is set as primary, update other payment methods to not be primary
    let updatedPaymentMethods = paymentMethods;
    if (newPaymentMethod.isPrimary) {
        updatedPaymentMethods = paymentMethods.map((pm) => (Object.assign(Object.assign({}, pm), { isPrimary: false })));
    }
    // Add the new payment method to the list
    const finalPaymentMethods = [...updatedPaymentMethods, newPaymentMethod];
    return Object.assign(Object.assign({}, currentData), { success: true, paymentMethods: finalPaymentMethods });
});
/**
 * Update an existing payment method via POST request.
 *
 * Endpoint: POST /UpdatePaymentMethod (legacy endpoint)
 *
 * Updates the payment method with the provided data.
 *
 * This function returns a PaymentMethodsResponse to properly update the SWR cache.
 * After the mutation, we optimistically update the payment method in the cache.
 *
 * @param key - SWR cache key (not used in mutation, but required by useSWRMutation)
 * @param options - Contains the payment method ID, request, and current cached data
 * @returns A PaymentMethodsResponse with the updated paymentMethods array
 */
export const updatePaymentMethod = (key, { arg, }) => __awaiter(void 0, void 0, void 0, function* () {
    var _b;
    // Use legacy endpoint with POST and payload (includes paymentMethodId in the request)
    const response = yield axiosPrivate.post("/UpdatePaymentMethod", Object.assign(Object.assign({}, arg.request), { paymentMethodId: arg.paymentMethodId }));
    if (!response.data || !response.data.success) {
        throw new Error(((_b = response.data) === null || _b === void 0 ? void 0 : _b.message) || "Failed to update payment method");
    }
    // Build the updated payment method from the response (legacy returns { success, message, card })
    const updatedPaymentMethod = response.data.card;
    // Update the cache optimistically by replacing the payment method in the current data
    const currentData = arg.currentData;
    if (!currentData) {
        // No current data, return a minimal response with just the updated payment method
        return {
            success: true,
            paymentMethods: [updatedPaymentMethod],
            giftCards: [],
            enableGiftCards: false,
        };
    }
    const paymentMethods = currentData.paymentMethods || [];
    const existingIndex = paymentMethods.findIndex((pm) => pm.id === arg.paymentMethodId);
    let updatedPaymentMethods;
    if (existingIndex !== -1) {
        // Update existing payment method
        updatedPaymentMethods = [...paymentMethods];
        updatedPaymentMethods[existingIndex] = updatedPaymentMethod;
    }
    else {
        // Payment method not found in cache, add it
        updatedPaymentMethods = [...paymentMethods, updatedPaymentMethod];
    }
    return Object.assign(Object.assign({}, currentData), { success: true, paymentMethods: updatedPaymentMethods });
});
/**
 * Set a payment method as primary via POST request.
 *
 * Endpoint: POST /UpdatePrimaryPaymentMethod (legacy endpoint)
 *
 * Sets the specified payment method as primary and clears the isPrimary flag
 * on any previously primary payment method.
 *
 * This function returns a PaymentMethodsResponse to properly update the SWR cache.
 * After the mutation, we optimistically update the primary status in the cache.
 *
 * @param key - SWR cache key (not used in mutation, but required by useSWRMutation)
 * @param options - Contains the payment method ID and current cached data
 * @returns A PaymentMethodsResponse with the updated paymentMethods array
 */
export const setPaymentMethodAsPrimary = (key, { arg }) => __awaiter(void 0, void 0, void 0, function* () {
    var _c;
    // Use legacy endpoint with POST and payload
    const response = yield axiosPrivate.post("/UpdatePrimaryPaymentMethod", {
        paymentMethodId: arg.paymentMethodId,
    });
    if (!response.data || !response.data.success) {
        throw new Error(((_c = response.data) === null || _c === void 0 ? void 0 : _c.message) || "Failed to set payment method as primary");
    }
    // Update the cache optimistically
    const currentData = arg.currentData;
    if (!currentData) {
        // No current data, just return success
        return {
            success: true,
            paymentMethods: [],
            giftCards: [],
            enableGiftCards: false,
        };
    }
    const paymentMethods = currentData.paymentMethods || [];
    // Update all payment methods: set the target as primary, clear others
    const updatedPaymentMethods = paymentMethods.map((pm) => (Object.assign(Object.assign({}, pm), { isPrimary: pm.id === arg.paymentMethodId })));
    return Object.assign(Object.assign({}, currentData), { success: true, paymentMethods: updatedPaymentMethods });
});
/**
 * Deactivate (soft delete) a payment method via POST request.
 *
 * Endpoint: POST /DeactivatePaymentMethod (legacy endpoint)
 *
 * Marks the payment method as inactive. This is a soft delete - the payment method
 * is not physically removed from the database.
 *
 * This function returns a PaymentMethodsResponse to properly update the SWR cache.
 * After the mutation, we optimistically remove the payment method from the cache.
 *
 * @param key - SWR cache key (not used in mutation, but required by useSWRMutation)
 * @param options - Contains the payment method ID and current cached data
 * @returns A PaymentMethodsResponse with the updated paymentMethods array
 */
export const deactivatePaymentMethod = (key, { arg }) => __awaiter(void 0, void 0, void 0, function* () {
    // Use legacy endpoint with POST and payload
    yield axiosPrivate.post("/DeactivatePaymentMethod", {
        paymentMethodId: arg.paymentMethodId,
    });
    // Update the cache optimistically by removing the payment method
    const currentData = arg.currentData;
    if (!currentData) {
        // No current data, just return success
        return {
            success: true,
            paymentMethods: [],
            giftCards: [],
            enableGiftCards: false,
        };
    }
    const paymentMethods = currentData.paymentMethods || [];
    // Remove the deactivated payment method from the list
    const updatedPaymentMethods = paymentMethods.filter((pm) => pm.id !== arg.paymentMethodId);
    return Object.assign(Object.assign({}, currentData), { success: true, paymentMethods: updatedPaymentMethods });
});
/**
 * Process payment method mutation errors to extract meaningful error messages.
 *
 * @param error - The error object from the mutation
 * @returns A processed error object or the original error
 */
export const processPaymentMethodError = (error) => {
    var _a, _b;
    if (axios.isAxiosError(error)) {
        const responseData = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data;
        if (responseData) {
            // Extract error message from various possible formats
            const message = responseData.message || "An error occurred";
            const errors = responseData.errors || [];
            const modelErrors = responseData.modelErrors ? Object.values(responseData.modelErrors).flat() : [];
            const allErrors = [message, ...errors, ...modelErrors].filter(Boolean);
            return {
                message: allErrors.join(", "),
                status: (_b = error.response) === null || _b === void 0 ? void 0 : _b.status,
                data: responseData,
            };
        }
    }
    return error;
};
